// Copyright 2025, University of Maryland, All Rights Reserved
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { UserTypeahead } from './UserTypeahead';
import { UserPlus } from 'lucide-react';

export type AddUserScope = 'all' | 'org' | 'workspace';

interface AddUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: AddUserScope;
  /** All known user IDs across the system, used as typeahead suggestions. */
  allUserIds: string[];
  /** User IDs already in the current scope (org members or workspace members). */
  scopeUserIds: string[];
  isPending: boolean;
  onSubmit: (data: { userId: string; role?: string }) => void;
}

const CONFLICT_MESSAGES: Record<AddUserScope, string> = {
  all: 'A user with that ID already exists.',
  org: 'That user is already a member of this organization.',
  workspace: 'That user is already a member of this workspace.',
};

// cm-coding: An administrator adding a person to an organization or workspace
// should grant only that requested membership. Explain that the backend may
// create a neutral system identity as a prerequisite; global user authority is
// a separate action and must not be implied by scoped onboarding.
const DESCRIPTIONS: Record<AddUserScope, string> = {
  all: 'Create a new user in the system.',
  org: 'Add an existing user to this organization, or enter a new user ID to create one.',
  workspace: 'Add an existing user to this workspace, or enter a new user ID to create one.',
};

export function AddUserDialog({
  open,
  onOpenChange,
  scope,
  allUserIds,
  scopeUserIds,
  isPending,
  onSubmit,
}: AddUserDialogProps) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<string>('member');

  useEffect(() => {
    if (open) {
      setUserId('');
      setRole('member');
    }
  }, [open]);

  const trimmed = userId.trim();
  const conflict = scope === 'all'
    ? allUserIds.includes(trimmed)
    : scopeUserIds.includes(trimmed);
  const canSubmit = !!trimmed && !conflict && !isPending;

  const showRoleSelect = scope === 'org' || scope === 'workspace';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({ userId: trimmed, role: showRoleSelect ? role : undefined });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="add-user-dialog"
        showCloseButton={false}
        className="w-[460px] max-w-[calc(100%-2rem)] gap-0 rounded-2xl border-0 p-[26px] shadow-[0_24px_64px_rgba(2,8,23,0.32)]"
      >
        <DialogHeader className="flex-row items-center gap-[13px] space-y-0 text-left">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-[11px] bg-[#f1f5f9] text-[#475569]">
            <UserPlus className="size-[19px]" />
          </span>
          <span className="flex flex-col gap-0.5">
            <DialogTitle className="text-[17px] font-bold leading-[normal] text-[#0f172a]">Add User</DialogTitle>
            <DialogDescription className="text-[13px] leading-[normal] text-[#64748b]">
              {DESCRIPTIONS[scope]}
            </DialogDescription>
          </span>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user-id" className="text-[13px] font-semibold leading-[normal] text-[#0f172a]">User ID</Label>
              <UserTypeahead
                value={userId}
                onValueChange={(value) => setUserId(value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                suggestions={allUserIds}
                conflictUserIds={scopeUserIds}
                placeholder="e.g. jsmith12"
                autoFocus
              />
              <p className="text-[12.5px] leading-[normal] text-[#94a3b8]">Lowercase letters, numbers, and hyphens only.</p>
              {trimmed && conflict && (
                <p className="text-xs text-destructive" data-testid="add-user-conflict">
                  {CONFLICT_MESSAGES[scope]}
                </p>
              )}
            </div>

            {showRoleSelect && (
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Qualify the scope — bare admin/member reads ambiguously
                        against global, org, and workspace authority levels. */}
                    <SelectItem value="member">
                      {scope === 'org' ? 'Org member' : 'Workspace member'}
                    </SelectItem>
                    <SelectItem value="admin">
                      {scope === 'org' ? 'Org admin' : 'Workspace admin'}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="mt-[22px] gap-2.5">
            <Button className="h-10 rounded-[9px] border-[#e2e8f0] bg-white px-[18px] text-sm font-medium text-[#334155] shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:border-[#cbd5e1] hover:bg-[#f8fafc]" type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button className="h-10 rounded-[9px] bg-[#111827] px-5 text-sm font-medium text-white shadow-[0_1px_2px_rgba(0,0,0,0.08)] transition-[background,transform] duration-150 hover:bg-[#1f2937] active:scale-[0.97]" type="submit" disabled={!canSubmit} data-testid="add-user-submit">
              {isPending ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
